import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import {
  PRACTICE_ONBOARDING_PATIENT_FOLLOW_UP,
  PracticeOnboardingTaskKey,
} from '../../models/practice-onboarding.model';
import { GuidedTourService } from '../../services/guided-tour.service';
import { PracticeOnboardingService } from '../../services/practice-onboarding.service';
import { ShellPathService } from '../../services/shell-path.service';
import { UserService } from '../../services/user';

interface ChecklistItem {
  key: PracticeOnboardingTaskKey;
  label: string;
  route: string[];
  optional?: boolean;
}

@Component({
  selector: 'app-practice-onboarding-checklist',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './practice-onboarding-checklist.html',
  styleUrl: './practice-onboarding-checklist.css',
})
export class PracticeOnboardingChecklist {
  private onboardingService = inject(PracticeOnboardingService);
  protected readonly guidedTourService = inject(GuidedTourService);
  private shellPath = inject(ShellPathService);
  protected readonly userService = inject(UserService);

  protected readonly showChecklist = this.onboardingService.showChecklist;
  protected readonly completedCount = this.onboardingService.completedCount;
  protected readonly totalCount = this.onboardingService.totalCount;
  protected readonly openCount = computed(
    () => Math.max(0, this.totalCount() - this.completedCount()),
  );

  protected readonly fabAriaLabel = computed(() => {
    const open = this.openCount();
    if (open === 1) {
      return $localize`:@@practiceOnboarding.openTasksAriaSingular:Show checklist, 1 open task`;
    }
    return $localize`:@@practiceOnboarding.openTasksAriaPlural:Show checklist, ${open}:count: open tasks`;
  });

  /** Manual collapse preference; tours force-collapse while active. */
  private readonly manuallyCollapsed = signal(false);
  private readonly collapsedBeforeTour = signal<boolean | null>(null);

  protected readonly isCollapsed = computed(
    () =>
      this.manuallyCollapsed() || this.guidedTourService.isTourActive(),
  );

  private readonly allItems: ChecklistItem[] = [
    {
      key: 'home',
      label: $localize`:@@practiceOnboarding.task.home:Get to know the dashboard`,
      route: ['/'],
    },
    {
      key: 'practiceDetails',
      label: $localize`:@@practiceOnboarding.task.practiceDetails:Add practice master data`,
      route: ['/practice-details'],
    },
    {
      key: 'employees',
      label: $localize`:@@practiceOnboarding.task.employees:Add employees`,
      route: ['/employees'],
      optional: true,
    },
    {
      key: 'patient',
      label: $localize`:@@practiceOnboarding.task.patient:Add a patient`,
      route: ['/'],
    },
    {
      key: 'patientStats',
      label: $localize`:@@practiceOnboarding.task.patientStats:Open patient statistics`,
      route: ['/'],
    },
    {
      key: 'editPatient',
      label: $localize`:@@practiceOnboarding.task.editPatient:Explore patient actions`,
      route: ['/patients'],
    },
  ];

  protected readonly items = computed<ChecklistItem[]>(() => {
    const visible = new Set(this.onboardingService.visibleTaskKeys());
    return this.allItems.filter((item) => visible.has(item.key));
  });

  constructor() {
    effect(() => {
      const tourActive = this.guidedTourService.isTourActive();
      if (tourActive) {
        if (this.collapsedBeforeTour() === null) {
          this.collapsedBeforeTour.set(this.manuallyCollapsed());
        }
        return;
      }
      const previous = this.collapsedBeforeTour();
      if (previous !== null) {
        this.manuallyCollapsed.set(previous);
        this.collapsedBeforeTour.set(null);
      }
    });
  }

  protected isCompleted(key: PracticeOnboardingTaskKey): boolean {
    return this.onboardingService.isTaskCompleted(key);
  }

  protected toggleCollapsed(event?: Event): void {
    event?.stopPropagation();
    if (this.guidedTourService.isTourActive()) {
      // Stay collapsed during tours so the panel cannot cover dialogs.
      return;
    }
    this.manuallyCollapsed.update((value) => !value);
  }

  protected onTaskClick(item: ChecklistItem): void {
    if (this.isCompleted(item.key)) {
      return;
    }
    if (
      PRACTICE_ONBOARDING_PATIENT_FOLLOW_UP.includes(item.key) &&
      !this.isCompleted('patient')
    ) {
      return;
    }

    this.onboardingService.queueTour(item.key);
    this.shellPath.navigateToRoute(item.route);
  }

  protected onSkipEmployees(event: MouseEvent): void {
    event.stopPropagation();
    if (this.isCompleted('employees')) {
      return;
    }
    this.onboardingService.skipEmployeesTask();
  }
}
